import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { dismissAttention, selectAttention } from '../redux/actions/fleetActions';
import { ageOf } from '../lib/attention';

import { T, INDICATOR, FONT_MONO } from '../constants/tokens';

/**
 * Severity dot color. Keys are the MUI Lab severities the alert and
 * announcement slices already use.
 */
const DOT = {
  error: INDICATOR.error,
  warning: INDICATOR.warning,
  success: INDICATOR.success,
  info: INDICATOR.accent,
  // Attention item tones.
  bad: INDICATOR.error,
  warn: INDICATOR.warning
};

/**
 * The header's "needs attention" bell and its popover: what needs attention
 * on campus (offline hubs, low badges, staff without a badge), the same list
 * as the Overview's panel. Choosing one opens where it can be dealt with.
 * Notices such as server errors are bars under the header (AlertList).
 */
function AttentionBell({ attention, dismissItem }) {
  const [open, setOpen] = useState(false);
  const history = useHistory();

  const items = [
    ...attention.map((a) => ({
      id: a.id,
      title: a.title,
      meta: a.meta,
      age: ageOf(a.since),
      severity: a.tone,
      open: () => {
        setOpen(false);
        history.push(a.link);
      },
      dismiss: () => dismissItem(a.id)
    }))
  ];

  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button
        type='button'
        aria-label={'Needs attention: ' + items.length}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        style={{
          width: 36,
          height: 36,
          borderRadius: 9999,
          border: '1px solid ' + T.hairline,
          background: open ? T.surfaceSoft : T.elevated,
          color: T.ink,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0
        }}
      >
        <svg
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.4'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M4 6.8a4 4 0 0 1 8 0c0 2.1.4 3.3 1 4.2H3c.6-.9 1-2.1 1-4.2Z' />
          <path d='M6.5 13.2a1.7 1.7 0 0 0 3 0' />
        </svg>
      </button>

      {items.length > 0 &&
        <span
          style={{
            position: 'absolute',
            top: -2,
            right: -2,
            minWidth: 17,
            height: 17,
            borderRadius: 9999,
            background: T.ink,
            color: T.canvas,
            border: '2px solid ' + T.canvas,
            fontFamily: FONT_MONO,
            fontSize: 10,
            lineHeight: '13px',
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          {items.length}
        </span>}

      {open &&
        <div
          style={{
            position: 'fixed',
            top: 62,
            right: 16,
            width: 'min(380px, calc(100vw - 32px))',
            maxHeight: 'min(70vh, 520px)',
            overflowY: 'auto',
            background: T.elevated,
            border: '1px solid ' + T.hairline,
            borderRadius: 6,
            zIndex: 1300
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '16px 16px 12px',
              borderBottom: '1px solid ' + T.hairlineSoft
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink }}>
              Needs attention
            </span>
            <span
              style={{
                background: T.hairlineSoft,
                borderRadius: 9999,
                padding: '2px 8px',
                fontSize: 12,
                fontWeight: 500,
                color: T.ink
              }}
            >
              {items.length}
            </span>
            <div style={{ flex: '1 0 8px' }} />
            <button
              type='button'
              aria-label='Close'
              onClick={() => setOpen(false)}
              style={{
                fontFamily: 'inherit',
                width: 24,
                height: 24,
                border: 0,
                background: 'transparent',
                color: T.body,
                fontSize: 16,
                cursor: 'pointer',
                borderRadius: 6,
                padding: 0
              }}
            >
              &times;
            </button>
          </div>

          {items.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 16px',
                borderBottom: '1px solid ' + T.hairlineSoft
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  flex: 'none',
                  marginTop: 6,
                  background: DOT[item.severity] || INDICATOR.accent
                }}
              />
              <span
                role={item.open ? 'button' : undefined}
                tabIndex={item.open ? 0 : undefined}
                onClick={item.open}
                onKeyDown={item.open ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    item.open();
                  }
                } : undefined}
                style={{ minWidth: 0, flex: 1, cursor: item.open ? 'pointer' : 'default' }}
              >
                <span
                  style={{
                    display: 'flex',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    color: T.ink
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
                  {item.age &&
                    <span style={{ flex: 'none', fontFamily: FONT_MONO, fontSize: 12, fontWeight: 400, color: T.muted }}>
                      {item.age}
                    </span>}
                </span>
                {item.meta &&
                  <span style={{ display: 'block', fontSize: 13, lineHeight: '18px', color: T.body }}>
                    {item.meta}
                  </span>}
              </span>
              {item.dismiss &&
                <button
                  type='button'
                  aria-label='Dismiss'
                  onClick={item.dismiss}
                  style={{
                    flex: 'none',
                    width: 24,
                    height: 24,
                    border: 0,
                    background: 'transparent',
                    color: T.muted,
                    fontSize: 16,
                    cursor: 'pointer',
                    padding: 0
                  }}
                >
                  &times;
                </button>}
            </div>
          ))}

          {items.length === 0 &&
            <div style={{ padding: 16, fontSize: 14, color: T.body }}>
              Nothing needs attention.
            </div>}
        </div>}
    </div>
  );
}

AttentionBell.propTypes = {
  attention: PropTypes.array.isRequired,
  dismissItem: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  attention: selectAttention(state)
});

const mapDispatchToProps = (dispatch) => ({
  dismissItem: (id) => dispatch(dismissAttention(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(AttentionBell);
